import React, { useState } from 'react';
import Modal from 'react-modal';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    CheckCircleIcon,
} from '@heroicons/react/24/solid';
import { HashLink } from 'react-router-hash-link';

const mediaItems = [
    { type: 'image', src: '/assets/01_Aster_Front_Drone_Cropped.jpg' },
    { type: 'image', src: '/assets/02_Aster_Front_Cropped.jpg' },
    { type: 'image', src: '/assets/03_Aster_Kitchen.PNG' },
    { type: 'image', src: '/assets/04_Aster_Single Bed.jpg' },
    { type: 'image', src: '/assets/05_Aster_Double Bed.jpg' },
];

const customStyles = {
    content: {
        inset: '50% auto auto 50%',
        transform: 'translate(-50%, -50%)',
        padding: 0,
        border: 'none',
        background: 'transparent',
        overflow: 'hidden',
        maxWidth: '90vw',
        maxHeight: '90vh',
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        zIndex: 1000,
    },
};

Modal.setAppElement('#root');

const LocationDetails = () => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    const openModal = (index) => {
        setSelectedImageIndex(index);
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
    };

    const prevImage = () => {
        setSelectedImageIndex(
            selectedImageIndex === 0
                ? mediaItems.length - 1
                : selectedImageIndex - 1
        );
    };

    const nextImage = () => {
        setSelectedImageIndex(
            selectedImageIndex === mediaItems.length - 1
                ? 0
                : selectedImageIndex + 1
        );
    };

    const features = [
        'Spacious living areas',
        'Modern kitchen facilities',
        'Outdoor recreational spaces',
        'Close proximity to support services',
        'Laundry facilities with washer and dryer',
        'Back patio with grill',
        '55" TV with Netflix and Sling subscriptions',
        'Private individual food storage cabinets',
        'Three refrigerators',
        'Three bathrooms (3-4 people per bathroom)',
    ];

    return (
        <section className="py-12">
            <div className="container mx-auto px-4">
                <h2
                    className="text-4xl font-heading font-bold text-primary mb-6"
                    data-aos="fade-up"
                >
                    Paradise Valley - Phoenix, AZ Location
                </h2>

                {/* Media Slider */}
                <div className="flex overflow-x-auto space-x-4 mb-8 snap-x snap-mandatory pb-4">
                    {mediaItems.map((item, index) => (
                        <div
                            key={index}
                            className="relative group cursor-pointer flex-shrink-0 w-64 snap-start"
                            onClick={() => openModal(index)}
                            data-aos="zoom-in"
                            data-aos-delay={index * 100}
                        >
                            {item.type === 'image' ? (
                                <img
                                    src={item.src}
                                    alt={`View ${index + 1}`}
                                    className="w-full h-64 object-cover rounded-md drop-shadow-2xl"
                                />
                            ) : (
                                <div className="w-full h-64 bg-black rounded-md drop-shadow-2xl relative">
                                    {/* Video Thumbnail */}
                                    <img
                                        src="/assets/thumbnail.png" // Replace with your video thumbnail
                                        alt="Video Thumbnail"
                                        className="w-full h-full object-cover rounded-md"
                                    />
                                    {/* Play Icon Overlay */}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <svg
                                            className="h-16 w-16 text-white"
                                            fill="currentColor"
                                            viewBox="0 0 84 84"
                                        >
                                            <circle
                                                cx="42"
                                                cy="42"
                                                r="42"
                                                opacity="0.7"
                                            />
                                            <polygon points="33,24 61,42 33,60" />
                                        </svg>
                                    </div>
                                </div>
                            )}
                            <div className="absolute inset-0 bg-black bg-opacity-30 opacity-0 group-hover:opacity-100 transition duration-300 rounded-md flex items-center justify-center">
                                <span className="text-white text-xl font-medium">
                                    {item.type === 'image'
                                        ? 'View Photo'
                                        : 'Play Video'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Modal for Media Preview */}
                {modalIsOpen && (
                    <Modal
                        isOpen={modalIsOpen}
                        onRequestClose={closeModal}
                        style={customStyles}
                        contentLabel="Media Preview"
                    >
                        <button
                            onClick={closeModal}
                            className="absolute top-4 right-4 text-white text-3xl font-bold focus:outline-none z-10"
                        >
                            &times;
                        </button>
                        <button
                            onClick={prevImage}
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white text-3xl focus:outline-none z-10"
                        >
                            <ChevronLeftIcon className="h-10 w-10" />
                        </button>
                        <button
                            onClick={nextImage}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white text-3xl focus:outline-none z-10"
                        >
                            <ChevronRightIcon className="h-10 w-10" />
                        </button>
                        {mediaItems[selectedImageIndex].type === 'image' ? (
                            <img
                                src={mediaItems[selectedImageIndex].src}
                                alt={`View ${selectedImageIndex + 1}`}
                                className="w-full h-auto max-h-screen object-contain"
                            />
                        ) : (
                            <video
                                src={mediaItems[selectedImageIndex].src}
                                controls
                                autoPlay
                                className="w-full h-auto max-h-screen object-contain"
                            />
                        )}
                    </Modal>
                )}

                {/* Location Description */}
                <div className="mt-12" data-aos="fade-up">
                    <p className="text-lg leading-relaxed mb-8">
                        Our Paradise Valley location offers a serene environment for
                        recovery. Nestled in the heart of Paradise Valley, it
                        provides easy access to local amenities while ensuring a
                        peaceful atmosphere for our residents. Located 10 minutes from NSFC and Studio 164.
                    </p>

                    <h3 className="text-2xl font-heading font-semibold text-primary mb-4">
                        Features of this Location
                    </h3>

                    {/* Features List */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className="flex items-start"
                                data-aos="fade-up"
                                data-aos-delay={index * 50}
                            >
                                <CheckCircleIcon className="h-6 w-6 text-secondary flex-shrink-0 mt-1 mr-3" />
                                <p className="text-lg">{feature}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Back to Locations Button */}
                <div className="mt-8">
                    <HashLink
                        smooth
                        to="/#locations"
                        className="inline-block px-6 py-3 bg-secondary text-white rounded-full text-lg font-medium hover:bg-red-600 transition duration-300"
                    >
                        Back
                    </HashLink>
                </div>
            </div>
        </section>
    );
};

export default LocationDetails;
